/**
 * Контекст возврата из PROCTOR.
 *
 * Прокторинг уводит участника со страницы обычным переходом, а возвращает на
 * адрес из своей настройки (`PROCTOR_RETURN_URL`, сейчас — корень сайта). По
 * корню неоткуда узнать, какую олимпиаду он писал, поэтому ID запоминается
 * перед уходом и читается после возврата.
 *
 * Хранится в `sessionStorage`, а не в `localStorage`: это контекст одного
 * похода, он не должен пережить закрытие вкладки и всплыть через неделю.
 *
 * Запись привязана к участнику. Полагаться на очистку при выходе нельзя —
 * `authTokens.clear()` вызывается из трёх мест (выход, провал инициализации,
 * провал refresh), и любое из них может не дойти сюда; а вот чужой `userId`
 * виден всегда, и такая запись просто игнорируется. Так следующий вошедший в
 * этой же вкладке не попадёт на чужую олимпиаду.
 *
 * Токенов и ссылки запуска здесь нет: одноразовый `redirect_url` не
 * сохраняется нигде (требование контракта).
 */
const STORAGE_KEY = 'proctor_return'

export interface ProctorReturnContext {
  olympiadId: number
  userId: number
}

/** Запомнить, куда вернуть участника. Вызывается перед переходом в PROCTOR. */
export function saveProctorReturn(context: ProctorReturnContext): void {
  // Хранилище может быть недоступно: приватный режим Safari, отключённые
  // данные сайта, iframe с ограничениями. Потерянный контекст — это лишний
  // шаг для участника, а исключение здесь уронило бы сам запуск олимпиады.
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context))
  } catch {
    /* контекст не сохранён — после возврата участник выберет олимпиаду сам */
  }
}

/** Контекст текущего участника, если он есть и принадлежит именно ему. */
export function readProctorReturn(userId: number | null | undefined): ProctorReturnContext | null {
  if (typeof userId !== 'number') return null

  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const { olympiadId, userId: storedUserId } = parsed as Partial<ProctorReturnContext>
    if (typeof olympiadId !== 'number' || !Number.isInteger(olympiadId) || olympiadId <= 0) {
      return null
    }
    if (storedUserId !== userId) return null
    return { olympiadId, userId }
  } catch {
    return null
  }
}

/** Забыть контекст — на выходе из аккаунта и после того, как он отработал. */
export function clearProctorReturn(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* нечего чистить — хранилище недоступно */
  }
}
